import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { DropdownFormField } from '@elements/FormField';
import type { DropdownFormFieldProps } from 'types/common/form-fields';
import { CommonUtility, type Address } from '@utility/common';
import { carrierServices } from '@utility/constants/common';
import {
  type ConnectedCarrierAccounts,
  ownAccountLast4,
} from '@utility/own-carrier-account';
import {
  DangerSmallBadge,
  FPFetchRateErrorTrigger,
  FPOptionOrganizationRender,
  FPOptionRateRenderer,
  FPOptionRender,
} from '../ShippingFields';

export type ProviderDropdownProps = DropdownFormFieldProps & {
  /** Semantic-era prop the kit dropdown does not take; accepted and ignored. */
  defaultOpen?: boolean;
  allowAdditions?: boolean;
  onAddItem?: (event: unknown, data: { value?: string }) => void;
};

export function ProviderDropdown({
  defaultOpen: _defaultOpen,
  ...props
}: ProviderDropdownProps) {
  return <DropdownFormField {...props} />;
}

export type FulfillmentProviderOption = {
  value: string;
  text: string;
  /** V2 only: the mark the ship-from picker shows for a marketplace service. */
  logoUrl?: string;
  item?: {
    loading?: boolean;
    rate?: number | string;
    packages?: unknown[];
    is_international?: boolean;
    isFBA?: boolean;
    country?: string;
    address?: Address;
    showOutOfStockBadge?: boolean;
    shipping_speed?: string[];
    integration_type?: string;
    items?: { available?: number }[];
  };
};

export type ProviderPackageRate = {
  rate?: number | string;
  error?: string;
  label?: boolean;
  credentials_id?: string | null;
  provider: { key: string; value: string };
  service: { key: string; value: string };
  package: { key: string; value: string };
};

export type ProviderRate = {
  loading?: boolean;
  rate?: number | string | null;
  fetchRateError?: string;
  packages?: Record<string, ProviderPackageRate[]>;
};

export type ProviderRateMap = Record<string, ProviderRate>;

export type DropdownOption = {
  key: number;
  value: string;
  text?: ReactNode;
  content?: ReactNode;
  disabled?: boolean;
  className?: string;
};

type FulfillmentProviderSelection = {
  id: string;
  service: string | null;
  packageValue: string | null;
  provider: string | null;
  credentials_id: string | null;
};

const normalizeFulfillmentProviderSelection = (
  value: Partial<FulfillmentProviderSelection>,
): FulfillmentProviderSelection => ({
  id: value?.id || '',
  service: value?.service ?? null,
  packageValue: value?.packageValue ?? null,
  provider: value?.provider ?? null,
  credentials_id: value?.credentials_id ?? null,
});

export const fulfillmentProviderSelectionToDropdownValue = value =>
  value?.id ? JSON.stringify(normalizeFulfillmentProviderSelection(value)) : '';

export const dropdownValueToFulfillmentProviderSelection = value =>
  normalizeFulfillmentProviderSelection(
    typeof value === 'string' ? JSON.parse(value) : value,
  );

function extractNumber(input: string): number {
  return Number.parseFloat(input.replace(/[^\d.-]/g, ''));
}

/** The organization behind the negotiated rates, as `useAuth` reports it. */
export type CurrentOrganization = {
  _id?: string;
  company_name?: string;
  logo?: { url?: string };
  test?: boolean;
};

export type UseProviderOptionsParams = {
  fpList: FulfillmentProviderOption[];
  providerRates: ProviderRateMap;
  rateCurrency?: string;
  currentOrg?: CurrentOrganization;
  /** The workspace's own carrier accounts, to mark the rates they quote. */
  connectedAccounts?: ConnectedCarrierAccounts;
};

/**
 * The Ship From options: one entry per provider, and under each the carrier's
 * own services when the provider quoted them, so a rate can be picked outright.
 */
export function useProviderOptions({
  fpList = [],
  providerRates = {},
  rateCurrency,
  currentOrg,
  connectedAccounts,
}: UseProviderOptionsParams) {
  const minRate = useMemo(() => {
    let lowestRate = `$999999999+`;
    Object.values(providerRates || {}).forEach(provider => {
      const rate = extractNumber(`${provider.rate}` || '');
      if (
        CommonUtility.isValidNumber(rate) &&
        Number(rate) < extractNumber(lowestRate || '')
      ) {
        lowestRate = `${provider.rate}`;
      }
    });
    return lowestRate;
  }, [providerRates]);

  const providerFetchRateErrorSummary = useMemo(() => {
    const details: { name: string; message: string }[] = [];
    if (!CommonUtility.isValidArray(fpList)) {
      return { count: 0, details, label: '' };
    }
    fpList.forEach(item => {
      const message = providerRates?.[item.value]?.fetchRateError;
      if (message) {
        details.push({ name: item.text, message });
      }
    });
    return {
      count: details.length,
      details,
      label: `Got ${details.length} error${details.length === 1 ? '' : 's'} when fetching rates`,
    };
  }, [fpList, providerRates]);

  const providerOptions = useMemo(() => {
    const list: DropdownOption[] = [];
    let i = 0;

    const sortedProviders = [...fpList].sort((a, b) => {
      if (!providerRates[a.value] && providerRates[b.value]) {
        return -1;
      }
      if (!providerRates[b.value] && providerRates[a.value]) {
        return 1;
      }
      if (!providerRates[b.value] && !providerRates[a.value]) {
        return 0;
      }
      return (
        CommonUtility.convertToNumber(providerRates[a.value].rate) -
        CommonUtility.convertToNumber(providerRates[b.value].rate)
      );
    });

    sortedProviders.forEach(item => {
      const value = {
        id: item.value,
        service: null,
        packageValue: null,
        provider: null,
        credentials_id: null,
      };
      const fetchRateError = providerRates[item.value]?.fetchRateError;

      if (!CommonUtility.isValidObject(providerRates?.[item.value]?.packages)) {
        list.push({
          content: (
            <FPOptionRender
              item={item}
              providerRates={providerRates}
              minRate={minRate}
            />
          ),
          /* What the closed dropdown shows, since `content` only renders in the menu. */
          text: item.text,
          value: fulfillmentProviderSelectionToDropdownValue(value),
          key: i,
        });
        i += 1;
        return;
      }

      list.push({
        content: (
          <FPOptionRender
            item={item}
            providerRates={providerRates}
            minRate={minRate}
          />
        ),
        value: fulfillmentProviderSelectionToDropdownValue(value),
        text: (
          <div className="sk:flex sk:items-center sk:justify-between sk:gap-2">
            <div className="sk:flex sk:items-center sk:gap-1">
              <span>{item.text}</span>
              {!!item?.item?.showOutOfStockBadge && (
                <DangerSmallBadge>OOS</DangerSmallBadge>
              )}
              {fetchRateError && (
                <FPFetchRateErrorTrigger message={fetchRateError} />
              )}
            </div>
            <span>
              {fetchRateError
                ? '—'
                : CommonUtility.currencyFormat(
                    providerRates[item.value]?.rate,
                    rateCurrency,
                  )}
            </span>
          </div>
        ),
        key: i,
      });
      i += 1;

      const packages = providerRates[item.value].packages || {};
      const carriersWithRates = Object.keys(packages).filter(
        carrier =>
          packages[carrier].filter(
            pack => !CommonUtility.isNotEmpty(pack.error),
          ).length > 0,
      );

      carriersWithRates.forEach(carrier => {
        list.push({
          content: <FPOptionOrganizationRender providerKey={carrier} />,
          value: `${item.value}-disabled`,
          disabled: true,
          className: 'fp-provider-header',
          key: i,
        });
        i += 1;

        packages[carrier]
          .filter(pack => !CommonUtility.isNotEmpty(pack.error))
          .forEach(packageItem => {
            list.push({
              content: (
                <FPOptionRateRenderer
                  packageItem={packageItem}
                  ownAccountLast4={ownAccountLast4(
                    carrier,
                    packageItem,
                    connectedAccounts,
                  )}
                  carrierName={
                    carrierServices[carrier]?.text || packageItem.provider.key
                  }
                  currentOrg={currentOrg}
                />
              ),
              value: fulfillmentProviderSelectionToDropdownValue({
                id: item.value,
                service: packageItem.service.value,
                packageValue: packageItem.package.value,
                provider: packageItem.provider.value,
                credentials_id: packageItem.credentials_id || null,
              }),
              text: (
                <div className="sk:flex sk:justify-between sk:gap-2">
                  <span>
                    {item.text} - {packageItem.provider.key} -{' '}
                    {packageItem.service.key}
                  </span>
                  <span>
                    {CommonUtility.currencyFormat(
                      packageItem?.rate || '-',
                      rateCurrency,
                    )}
                  </span>
                </div>
              ),
              disabled: packageItem.label === false,
              key: i,
            });
            i += 1;
          });
      });
    });
    return list;
  }, [
    fpList,
    minRate,
    providerRates,
    currentOrg,
    rateCurrency,
    connectedAccounts,
  ]);

  return { providerOptions, providerFetchRateErrorSummary };
}
